import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Heart, MapPin, Loader2, Shield, ExternalLink } from "lucide-react";
import { api } from "@/api/client";
import { logger } from "@/utils/logger";

const BrowseShelters = ({ user, login }) => {
  const [shelters, setShelters] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchShelters();
  }, []);

  const fetchShelters = async () => {
    try {
      const res = await api.get("/shelters/browse");
      setShelters(res.data.shelters || []);
    } catch (err) {
      logger.error("Failed to fetch shelters:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = (e) => {
    if (!user) {
      e.preventDefault();
      login();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-12 px-6 md:px-12" data-testid="browse-shelters">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-[#22C55E]/10 flex items-center justify-center">
              <Heart className="w-5 h-5 text-[#22C55E]" />
            </div>
            <h1 className="text-3xl text-[#EDEDED]" style={{ fontFamily: "'Playfair Display', serif" }}>
              Shelters & Rescues
            </h1>
          </div>
          <p className="text-[#A1A1AA] text-sm ml-[52px]">
            Verified shelters and rescue organizations with animals available for adoption.
          </p>
        </div>

        {shelters.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <Heart className="w-12 h-12 text-[#3F3F46] mx-auto mb-4" />
            <h3 className="text-xl text-[#EDEDED] mb-2">No Shelters Listed Yet</h3>
            <p className="text-[#71717A] text-sm mb-6 max-w-md mx-auto">
              Verified shelters and rescue organizations will appear here once they complete registration and verification.
            </p>
            <Link
              to="/shelter/register"
              onClick={handleRegister}
              className="btn-gold inline-flex items-center gap-2 px-6 py-3 text-sm"
              data-testid="register-shelter-btn"
            >
              Register Your Shelter
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {shelters.map((shelter) => (
              <Link
                key={shelter.shelter_id}
                to={`/shelters/${shelter.shelter_id}`}
                className="glass-card p-5 hover:border-[#22C55E]/30 transition-all group"
                data-testid={`shelter-card-${shelter.shelter_id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-full bg-[#22C55E]/10 flex items-center justify-center">
                      <Heart className="w-5 h-5 text-[#22C55E]" />
                    </div>
                    <div>
                      <h3 className="text-[#EDEDED] font-medium group-hover:text-[#22C55E] transition-colors">
                        {shelter.organization_name}
                      </h3>
                      {shelter.is_verified && (
                        <div className="flex items-center gap-1 text-xs text-[#22C55E]">
                          <Shield className="w-3 h-3" /> Verified
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {shelter.description && (
                  <p className="text-sm text-[#A1A1AA] line-clamp-2 mb-3">{shelter.description}</p>
                )}

                <div className="flex items-center justify-between text-xs text-[#71717A]">
                  <div className="flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    <span>{shelter.city}{shelter.state ? `, ${shelter.state}` : ""}</span>
                  </div>
                  <span className="text-[#D4AF37]">
                    {shelter.listing_count || 0} animal{shelter.listing_count !== 1 ? "s" : ""} available
                  </span>
                </div>

                {shelter.website && (
                  <div className="mt-3 pt-3 border-t border-white/5">
                    <span className="text-xs text-[#52525B] flex items-center gap-1">
                      <ExternalLink className="w-3 h-3" /> {shelter.website}
                    </span>
                  </div>
                )}
              </Link>
            ))}
          </div>
        )}

        {/* CTA for shelters */}
        <div className="mt-12 text-center">
          <p className="text-[#71717A] text-sm mb-3">Are you a shelter or rescue organization?</p>
          <Link
            to="/shelter/register"
            onClick={handleRegister}
            className="text-[#D4AF37] text-sm hover:underline"
          >
            Register your organization on Breedling
          </Link>
        </div>
      </div>
    </div>
  );
};

export default BrowseShelters;
